/**
 * Chart data filtering utilities
 * Extracted from Chart.tsx to improve maintainability
 */

import type { ChartData } from "app/pages/industry-analysis/overview/utils/types";

interface FilterChartDataParams {
    rawAreaGraphData: ChartData;
    legendItems: any[];
    isPeriodOverPeriod: boolean;
}

/**
 * Filters chart data based on visible legend items
 * Handles both regular and period-over-period modes
 */
export const filterChartData = ({
    rawAreaGraphData,
    legendItems,
    isPeriodOverPeriod,
}: FilterChartDataParams): ChartData => {
    if (!rawAreaGraphData) {
        return rawAreaGraphData;
    }

    // If no legend items (Total-only case), show all data
    if (!legendItems || legendItems.length === 0) {
        return rawAreaGraphData;
    }

    if (isPeriodOverPeriod) {
        // For POP mode, we use period names, not device names
        // Since we only show "All traffic" (Total), we show both periods if Total is visible
        const totalLegendItem = legendItems.find((item) => item.name === "Total" || item.metric === "Total");

        if (totalLegendItem && !totalLegendItem.visible) {
            return [];
        }

        return rawAreaGraphData; // Show all period series if Total is visible
    } else {
        // Regular filtering for non-POP mode
        const visibleLegendIds = new Set(
            legendItems.filter((item) => item.visible).map((item) => item.metric || item.name),
        );

        // Create additional mappings for common name variations
        const nameVariations = new Set(visibleLegendIds);
        visibleLegendIds.forEach((id) => {
            // Handle "MobileWeb" <-> "Mobile Web" conversion
            if (id === "MobileWeb") {
                nameVariations.add("Mobile Web");
            } else if (id === "Mobile Web") {
                nameVariations.add("MobileWeb");
            }
        });

        const filtered = rawAreaGraphData.filter((series) => nameVariations.has(series.name));
        return filtered;
    }
};

/**
 * Checks if the chart has valid data to display
 */
const hasValidChartData = (filteredAreaGraphData: ChartData): boolean => {
    return !!(
        filteredAreaGraphData &&
        filteredAreaGraphData.length > 0 &&
        filteredAreaGraphData.some((series) => Array.isArray(series.data) && series.data.length > 0)
    );
};
